import { StandardEvents } from '@shopify/events';

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* -------------------------------------------------------------------------- */
/* Shared helpers, exposed for components that load as separate modules       */
/* -------------------------------------------------------------------------- */

/** @type {WeakMap<HTMLElement, number>} */
const tickTimers = new WeakMap();

/**
 * Replays the add-to-bag checkmark burst inside a `.tick-host`.
 * @param {Element | null | undefined} host
 * @param {{hold?: number}} [options] - Clears the tick after this many ms; 0 keeps it.
 */
function playTick(host, { hold = 0 } = {}) {
  if (!(host instanceof HTMLElement)) return;
  clearTimeout(tickTimers.get(host));
  host.removeAttribute('data-added');
  // Two frames so the cleared state is painted and the transitions replay.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      host.setAttribute('data-added', 'true');
      if (hold > 0) tickTimers.set(host, window.setTimeout(() => host.removeAttribute('data-added'), hold));
    });
  });
}

/** @param {Element | null | undefined} element */
function shake(element) {
  if (!(element instanceof HTMLElement) || reducedMotion()) return;
  element.animate(
    [
      { transform: 'translateX(0)' },
      { transform: 'translateX(-6px)' },
      { transform: 'translateX(5px)' },
      { transform: 'translateX(-3px)' },
      { transform: 'translateX(2px)' },
      { transform: 'translateX(0)' },
    ],
    { duration: 380, easing: 'ease-out' }
  );
}

/** Draws the eye to text that just changed. @param {Element | null | undefined} element */
function revealChange(element) {
  if (!(element instanceof HTMLElement) || reducedMotion()) return;
  element.animate(
    [
      { opacity: 0, transform: 'translateY(4px)' },
      { opacity: 1, transform: 'none' },
    ],
    { duration: 280, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
  );
}

window.AltiorMotion = { playTick, shake, revealChange };

/* -------------------------------------------------------------------------- */
/* Server-rendered success messages play their tick on arrival                */
/* -------------------------------------------------------------------------- */

document.querySelectorAll('[data-tick-autoplay]').forEach((host) => playTick(host));

/* -------------------------------------------------------------------------- */
/* Invalid email or pincode: a short shake alongside the browser's own hint   */
/* -------------------------------------------------------------------------- */

document.addEventListener(
  'invalid',
  (event) => {
    const field = event.target;
    if (!(field instanceof HTMLInputElement)) return;
    shake(field.closest('.club-gate__input-group, .delivery-estimate__field') ?? field);
  },
  true
);

/* -------------------------------------------------------------------------- */
/* Check out: Razorpay's checkout takes a moment to appear, so say it's coming */
/* -------------------------------------------------------------------------- */

const CHECKOUT_PENDING_TIMEOUT_MS = 12_000;

/** @type {{pill: HTMLElement, button: HTMLElement, spinner: HTMLElement, poll: number, timeout: number} | null} */
let checkoutPending = null;

function checkoutOverlayVisible() {
  return Array.from(document.querySelectorAll('iframe')).some((frame) => {
    if (!/razorpay/i.test(`${frame.className} ${frame.src}`)) return false;
    const rect = frame.getBoundingClientRect();
    const style = getComputedStyle(frame);
    return (
      rect.width > 200 &&
      rect.height > 200 &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      Number(style.opacity) > 0
    );
  });
}

/** @param {{fade?: boolean}} [options] - Checkout covering the screen removes it instantly. */
function endCheckoutPending({ fade = false } = {}) {
  if (!checkoutPending) return;
  const { pill, button, spinner, poll, timeout } = checkoutPending;
  checkoutPending = null;
  clearInterval(poll);
  clearTimeout(timeout);
  button.removeAttribute('data-pending');
  spinner.remove();
  if (!fade || reducedMotion()) {
    pill.remove();
    return;
  }
  pill.dataset.leaving = 'true';
  window.setTimeout(() => pill.remove(), 200);
}

/** @param {HTMLElement} button */
function startCheckoutPending(button) {
  if (checkoutPending) return;

  const pill = document.createElement('div');
  pill.className = 'checkout-pending';
  pill.setAttribute('role', 'status');
  pill.innerHTML =
    '<span class="checkout-pending__spinner" aria-hidden="true"></span><span>Opening secure checkout…</span>';
  document.body.append(pill);

  const spinner = document.createElement('span');
  spinner.className = 'checkout-pending__spinner checkout-pending__spinner--button';
  spinner.setAttribute('aria-hidden', 'true');
  button.append(spinner);
  button.setAttribute('data-pending', 'true');

  checkoutPending = {
    pill,
    button,
    spinner,
    // Tight poll: the pill must be gone the moment checkout covers the screen.
    poll: window.setInterval(() => {
      if (checkoutOverlayVisible()) endCheckoutPending();
    }, 50),
    timeout: window.setTimeout(() => endCheckoutPending({ fade: true }), CHECKOUT_PENDING_TIMEOUT_MS),
  };
}

// Window capture runs before Razorpay's own handler, which stops the click
// from bubbling. Observes only: never prevents or stops it.
window.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest('#checkout') : null;
    if (button instanceof HTMLButtonElement && !button.disabled) startCheckoutPending(button);
  },
  true
);
window.addEventListener('pagehide', () => endCheckoutPending());
document.addEventListener('visibilitychange', () => {
  if (document.hidden) endCheckoutPending();
});

/* -------------------------------------------------------------------------- */
/* Cart: briefly highlight the line that was just added                       */
/* -------------------------------------------------------------------------- */

/** @param {string} variantId */
function highlightCartLine(variantId) {
  if (reducedMotion()) return;
  const selector = variantId ? `[data-key^="${CSS.escape(variantId)}:"]` : 'cart-items-component [data-key]';
  const rows = variantId
    ? document.querySelectorAll(selector)
    : [document.querySelector(selector)].filter(Boolean);

  rows.forEach((row) => {
    if (!(row instanceof HTMLElement)) return;
    row.animate([{ backgroundColor: 'rgb(17 38 23 / 0.1)' }, { backgroundColor: 'rgb(17 38 23 / 0)' }], {
      duration: 1600,
      easing: 'ease-out',
    });
  });
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const update = /** @type {any} */ (event);
  if (update.action !== 'add' || !update.promise) return;

  const line = update.lines?.[0] ?? update.detail?.lines?.[0];
  const variantId = String(line?.merchandiseId ?? '').split('/').pop() ?? '';

  update.promise
    .then((/** @type {any} */ result) => {
      if (result?.detail?.didError) return;
      // After the drawer's delayed open (750ms) and its slide-in.
      window.setTimeout(() => highlightCartLine(variantId), 1150);
    })
    .catch(() => {});
});

/* -------------------------------------------------------------------------- */
/* Transparent header takes the tone of the hero slide beneath it             */
/* -------------------------------------------------------------------------- */

/** @param {Element | null | undefined} slide */
function applyHeroHeaderTone(slide) {
  const tone = slide?.getAttribute('data-header-tone');
  if (tone) {
    document.documentElement.dataset.heroHeaderTone = tone;
  } else {
    delete document.documentElement.dataset.heroHeaderTone;
  }
}

// Only the first section sits under a transparent header.
const isHeroSlide = (/** @type {Element} */ slide) =>
  Boolean(slide.closest('.content-for-layout > .shopify-section:first-child'));

document.addEventListener('slideshow:select', (event) => {
  const slide = /** @type {any} */ (event).detail?.slide;
  if (slide instanceof Element && isHeroSlide(slide)) applyHeroHeaderTone(slide);
});

applyHeroHeaderTone(
  document.querySelector('.content-for-layout > .shopify-section:first-child slideshow-slide[aria-hidden="false"]')
);

/* ------------------------------------------------------------------------ */
/* Hero video: decode only while it is the slide on screen.                  */
/*                                                                          */
/* slideshow.js never touches <video> - it has no reference to one at all -  */
/* so an autoplaying, looping hero video kept decoding behind the other      */
/* slide, and kept decoding after the hero had scrolled out of view. That is */
/* main-thread and GPU work for something nobody can see, and it competes    */
/* with the slide transition itself, which is where it was noticeable on a   */
/* slower phone.                                                            */
/*                                                                          */
/* Visibility is measured from the slide geometry rather than aria-hidden,   */
/* so it stays correct mid-swipe, when two slides are partly on screen.      */
/* ------------------------------------------------------------------------ */

const heroSection = document.querySelector('.content-for-layout > .shopify-section:first-child');

if (heroSection) {
  // Only videos that asked to autoplay. One a visitor paused deliberately is left alone.
  const heroVideos = () => Array.from(heroSection.querySelectorAll('slideshow-slide video[autoplay]'));

  let heroOnScreen = true;
  let syncQueued = false;

  const slideMostlyVisible = (slide) => {
    const track = slide.closest('slideshow-slides');
    if (!track) return true;
    const t = track.getBoundingClientRect();
    const s = slide.getBoundingClientRect();
    if (!s.width) return false;
    const overlap = Math.min(t.right, s.right) - Math.max(t.left, s.left);
    return overlap > s.width * 0.5;
  };

  const syncHeroVideo = () => {
    syncQueued = false;
    for (const video of heroVideos()) {
      const slide = video.closest('slideshow-slide');
      const shouldPlay = heroOnScreen && !document.hidden && slide != null && slideMostlyVisible(slide);
      if (shouldPlay === !video.paused) continue;
      if (shouldPlay) {
        const played = video.play();
        if (played && typeof played.catch === 'function') played.catch(() => {});
      } else {
        video.pause();
      }
    }
  };

  const queueSync = () => {
    if (syncQueued) return;
    syncQueued = true;
    requestAnimationFrame(syncHeroVideo);
  };

  document.addEventListener('slideshow:select', queueSync);
  document.addEventListener('visibilitychange', queueSync);

  for (const track of heroSection.querySelectorAll('slideshow-slides')) {
    track.addEventListener('scroll', queueSync, { passive: true });
  }

  if ('IntersectionObserver' in window) {
    new IntersectionObserver(
      (entries) => {
        for (const entry of entries) heroOnScreen = entry.isIntersecting;
        queueSync();
      },
      { threshold: 0.05 }
    ).observe(heroSection);
  }

  queueSync();
}

/* ------------------------------------------------------------------------ */
/* Added to Bag: a compact bottom sheet on mobile, instead of the full       */
/* cart drawer taking over the screen.                                       */
/*                                                                          */
/* cart-drawer.js only auto-opens the full drawer while its host element     */
/* carries the auto-open attribute -- read live, on every add. Removing it   */
/* once here, at load rather than inside an event handler, means there is no */
/* race over which of the two cartLinesUpdate listeners (its or this file's) */
/* happens to run first: by the time a real add can possibly fire, the       */
/* attribute is already gone. The header cart icon's own click-to-open path  */
/* is untouched, so "View bag" below still opens the exact same drawer.      */
/* ------------------------------------------------------------------------ */

const MOBILE_QUERY = '(max-width: 749px)';
const cartDrawerComponent = document.querySelector('cart-drawer-component');

if (cartDrawerComponent) {
  // auto-open is itself conditional server-side (settings.auto_open_cart_drawer),
  // so its presence at load -- not an assumption that it's always on -- is
  // what "desktop" restores to. This only ever takes the attribute away; it
  // never grants auto-open to a store that has the setting turned off.
  const hadAutoOpen = cartDrawerComponent.hasAttribute('auto-open');
  const mobileMedia = window.matchMedia(MOBILE_QUERY);
  const syncAutoOpen = (/** @type {{matches: boolean}} */ { matches: isMobile }) => {
    if (isMobile) {
      cartDrawerComponent.removeAttribute('auto-open');
    } else if (hadAutoOpen) {
      cartDrawerComponent.setAttribute('auto-open', '');
    }
  };
  syncAutoOpen(mobileMedia);
  mobileMedia.addEventListener('change', syncAutoOpen);
}

/** @type {HTMLDialogElement | null} */
let addedToBagDialog = null;

function buildAddedToBagSheet() {
  const dialog = /** @type {HTMLDialogElement} */ (document.createElement('dialog'));
  dialog.className = 'added-to-bag-sheet';

  const tickSource = document.querySelector('.checkmark-burst');

  dialog.innerHTML = [
    '<div class="added-to-bag-sheet__panel">',
    '<span class="added-to-bag-sheet__handle" aria-hidden="true"></span>',
    '<button type="button" class="added-to-bag-sheet__close" aria-label="Close"><span aria-hidden="true">&times;</span></button>',
    '<div class="added-to-bag-sheet__header">',
    '<span class="tick-host added-to-bag-sheet__tick"></span>',
    '<p class="added-to-bag-sheet__title">Added to your bag</p>',
    '</div>',
    '<a class="added-to-bag-sheet__item">',
    '<span class="added-to-bag-sheet__image-frame"><img class="added-to-bag-sheet__image" alt="" width="72" height="96"></span>',
    '<span class="added-to-bag-sheet__info">',
    '<span class="added-to-bag-sheet__name"></span>',
    '<span class="added-to-bag-sheet__variant"></span>',
    '<span class="added-to-bag-sheet__price"></span>',
    '</span>',
    '</a>',
    '<div class="added-to-bag-sheet__bundle" hidden>',
    '<span class="added-to-bag-sheet__stack"></span>',
    '<span class="added-to-bag-sheet__info">',
    '<span class="added-to-bag-sheet__name"></span>',
    '<span class="added-to-bag-sheet__variant"></span>',
    '<span class="added-to-bag-sheet__price"></span>',
    '</span>',
    '</div>',
    '<div class="added-to-bag-sheet__actions">',
    '<button type="button" class="button button-secondary added-to-bag-sheet__continue">Shop more</button>',
    '<button type="button" class="button added-to-bag-sheet__view-cart">View bag</button>',
    '</div>',
    '</div>',
  ].join('');

  const tickHost = dialog.querySelector('.added-to-bag-sheet__tick');
  if (tickHost && tickSource) tickHost.appendChild(tickSource.cloneNode(true));

  document.body.appendChild(dialog);

  // A tap on the backdrop closes it, same as any other dialog on this site.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
  dialog.querySelector('.added-to-bag-sheet__close')?.addEventListener('click', () => dialog.close());
  dialog.querySelector('.added-to-bag-sheet__continue')?.addEventListener('click', () => dialog.close());
  dialog.querySelector('.added-to-bag-sheet__view-cart')?.addEventListener('click', () => {
    dialog.close();
    const trigger = /** @type {HTMLElement | null} */ (document.querySelector('[data-testid="cart-drawer-trigger"]'));
    // On the rare storefront running settings.cart_type != 'drawer', there is
    // no trigger button -- the header cart is a plain link to /cart instead,
    // which is exactly where this should send the shopper anyway.
    if (trigger) {
      trigger.click();
    } else {
      window.location.assign('/cart');
    }
  });

  return dialog;
}

/**
 * The current PDP variant's price is already rendered, in the shop's own
 * configured currency format, right next to whichever Add to Bag button was
 * used. Reading it directly sidesteps reimplementing Shopify's money
 * formatting (and the format string that lives in shop settings, which this
 * file has no way to reach) purely to redraw a value already on screen.
 */
function currentDisplayedPrice() {
  // snippets/price.liquid always renders both a .price__regular and a
  // .price__sale block, toggling which is hidden, and wraps each visible
  // amount in a "Sale price " / "Regular price " .visually-hidden label for
  // screen readers -- real text nodes, so plain textContent picks them up
  // along with the number. Reading .price-item--sale or .price directly
  // skips both that label and the struck-through compare-at price beside it.
  const sources = [
    '.sticky-add-to-cart__price .price-item--sale',
    '.sticky-add-to-cart__price .price__regular:not(.price__hidden) .price',
    '.product-details price-component .price-item--sale',
    '.product-details price-component .price__regular:not(.price__hidden) .price',
  ];
  for (const selector of sources) {
    const text = document.querySelector(selector)?.textContent?.replace(/\s+/g, ' ').trim();
    if (text) return text;
  }
  return '';
}

/** @param {any} item - A line item from the Ajax Cart API (/cart.js shape). */
function showAddedToBagSheet(item) {
  if (!addedToBagDialog) addedToBagDialog = buildAddedToBagSheet();
  const dialog = addedToBagDialog;

  const link = /** @type {HTMLAnchorElement | null} */ (dialog.querySelector('.added-to-bag-sheet__item'));
  const img = /** @type {HTMLImageElement | null} */ (link?.querySelector('.added-to-bag-sheet__image') ?? null);
  const name = link?.querySelector('.added-to-bag-sheet__name');
  const variant = link?.querySelector('.added-to-bag-sheet__variant');
  const price = link?.querySelector('.added-to-bag-sheet__price');
  const title = dialog.querySelector('.added-to-bag-sheet__title');
  if (title) title.textContent = 'Added to your bag';
  if (link) link.hidden = false;
  const bundleBlock = /** @type {HTMLElement | null} */ (dialog.querySelector('.added-to-bag-sheet__bundle'));
  if (bundleBlock) bundleBlock.hidden = true;

  if (img && item.image) {
    // Shopify's cart image URLs accept the same _{width}x sizing suffix as
    // any other product image CDN URL.
    img.src = String(item.image).replace(/(\.[a-z]+)(\?.*)?$/i, '_180x$1$2');
    img.alt = item.title ?? '';
  }
  if (name) name.textContent = item.product_title ?? item.title ?? '';
  if (variant) {
    const isDefault = /^default title$/i.test((item.variant_title ?? '').trim());
    variant.textContent = isDefault ? '' : item.variant_title ?? '';
    variant.hidden = isDefault;
  }
  if (price) price.textContent = currentDisplayedPrice();
  if (link) link.href = item.url ?? '/cart';

  if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();

  const tickHost = dialog.querySelector('.tick-host');
  playTick(tickHost, { hold: 0 });
}

/**
 * A "Build your three" set: one confirmation for the whole set, with the
 * three tees stacked, what was picked, and the set price.
 * @param {any} bundle - { label, price, picks: [{ id, color, size }] } from the builder.
 * @param {any[]} items - Cart line items (/cart.js shape), for the images.
 */
function showAddedBundleSheet(bundle, items) {
  if (!addedToBagDialog) addedToBagDialog = buildAddedToBagSheet();
  const dialog = addedToBagDialog;
  const picks = Array.isArray(bundle?.picks) ? bundle.picks : [];

  const title = dialog.querySelector('.added-to-bag-sheet__title');
  if (title) title.textContent = `${picks.length || 3} tees added to your bag`;

  const single = /** @type {HTMLElement | null} */ (dialog.querySelector('.added-to-bag-sheet__item'));
  if (single) single.hidden = true;
  const block = /** @type {HTMLElement | null} */ (dialog.querySelector('.added-to-bag-sheet__bundle'));
  if (!block) return;
  block.hidden = false;

  const stack = block.querySelector('.added-to-bag-sheet__stack');
  if (stack) {
    stack.replaceChildren(
      ...picks.slice(0, 3).map((/** @type {any} */ pick) => {
        const line = items.find((/** @type {any} */ i) => String(i.variant_id) === String(pick.id));
        const frame = document.createElement('span');
        frame.className = 'added-to-bag-sheet__image-frame';
        const img = document.createElement('img');
        img.className = 'added-to-bag-sheet__image';
        img.alt = '';
        img.width = 72;
        img.height = 96;
        if (line?.image) img.src = String(line.image).replace(/(\.[a-z]+)(\?.*)?$/i, '_180x$1$2');
        frame.append(img);
        return frame;
      })
    );
  }
  const name = block.querySelector('.added-to-bag-sheet__name');
  if (name) name.textContent = bundle?.label ?? 'Your set';
  const variant = block.querySelector('.added-to-bag-sheet__variant');
  if (variant) variant.textContent = picks.map((/** @type {any} */ p) => `${p.color} ${p.size}`).join(' · ');
  const price = block.querySelector('.added-to-bag-sheet__price');
  if (price) price.textContent = bundle?.price ?? '';

  if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
  playTick(dialog.querySelector('.tick-host'), { hold: 0 });
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const update = /** @type {any} */ (event);
  if (update.action !== 'add' || !update.promise) return;
  if (!window.matchMedia(MOBILE_QUERY).matches) return;
  // The compact confirmation exists to avoid taking someone away from the
  // page they were shopping on. On /cart itself that page already is the
  // full cart, so there is nothing for this to usefully stand in for.
  if (window.location.pathname.replace(/\/$/, '') === '/cart') return;

  const line = update.lines?.[0] ?? update.detail?.lines?.[0];
  const merchandiseId = String(line?.merchandiseId ?? '').split('/').pop();

  update.promise
    .then((/** @type {any} */ result) => {
      if (result?.detail?.didError) return;
      const items = result?.detail?.items ?? [];
      if (result?.detail?.bundle) {
        showAddedBundleSheet(result.detail.bundle, items);
        return;
      }
      const added =
        items.find((/** @type {any} */ candidate) => String(candidate.variant_id) === merchandiseId) ??
        items[items.length - 1];
      if (added) showAddedToBagSheet(added);
    })
    .catch(() => {});
});

/* -------------------------------------------------------------------------- */
/* #the-fabric deep link                                                       */
/* -------------------------------------------------------------------------- */

// The homepage's "See the full fabric story" button lands on the product
// page's "04 The fabric" chapter. The chapter is a theme section whose id
// carries the template number, so the stable link is a friendly hash that
// resolves to the section by its handle.
function scrollToFabricChapter() {
  if (window.location.hash !== '#the-fabric') return;
  const target = document.querySelector('[id$="__ed_thesis"]');
  if (!target) return;

  const headerHeight = document.querySelector('#header-component')?.getBoundingClientRect().height ?? 0;
  target.style.scrollMarginTop = `${Math.round(headerHeight)}px`;
  target.scrollIntoView({ block: 'start', behavior: 'auto' });
}

window.addEventListener('hashchange', scrollToFabricChapter);
window.addEventListener('load', () => {
  scrollToFabricChapter();
  // Images above the chapter settle after load and shift it; correct once more.
  setTimeout(scrollToFabricChapter, 700);
});

/* -------------------------------------------------------------------------- */
/* Hero scroll cue (phones)                                                    */
/* -------------------------------------------------------------------------- */

// On phones the hero is a full screen with no button; a small "Scroll" cue at
// its foot invites the first swipe, and a tap takes the visitor straight to the
// first section below. CSS hides it on larger screens.
{
  const hero = document.querySelector('.content-for-layout > .shopify-section[id$="__hero_slideshow"]');
  if (hero && !hero.querySelector('.hero-scroll-cue')) {
    const cue = document.createElement('button');
    cue.type = 'button';
    cue.className = 'hero-scroll-cue';
    cue.setAttribute('aria-label', 'Scroll to the collection');
    cue.innerHTML = '<span>Scroll</span><span class="hero-scroll-cue__line" aria-hidden="true"></span>';
    cue.addEventListener('click', (event) => {
      // A page-level click handler on the hero otherwise cancels the scroll.
      event.stopPropagation();
      // The header slides away on scroll down, so land exactly below the hero.
      const top = hero.getBoundingClientRect().bottom + window.scrollY;
      // Native smooth scrolling gets cancelled on this page, so step it by hand.
      if (reducedMotion()) {
        window.scrollTo({ top, behavior: 'instant' });
        return;
      }
      const start = window.scrollY;
      const distance = top - start;
      const duration = 700;
      const t0 = performance.now();
      const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
      const step = (now) => {
        const t = Math.min(1, (now - t0) / duration);
        window.scrollTo({ top: start + distance * ease(t), behavior: 'instant' });
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
    if (getComputedStyle(hero).position === 'static') hero.style.position = 'relative';
    hero.append(cue);
  }
}
